import { ArrowForwardIos, FiberManualRecord } from '@mui/icons-material';
import { IconButton, Typography, useTheme } from '@mui/material';
import { ScreenType } from '@tmdb-graphql-api/schema/types';
import { useMemo } from 'react';
import { Link } from '#components/Link/index.tsx';
import { Section } from '#components/Section/index.tsx';
import { useIsAncestor } from '#contexts/Ancestors/index.ts';
import { Ancestor } from '#enums/ancestors.ts';
import { ImageSize, PosterSize } from '#enums/imageSizes.ts';
import { SectionType } from '#enums/mediaTypes.ts';
import { buildImagePath } from '#helpers/buildImagePath.ts';
import { formatAge } from '#helpers/formatAge.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { Divider } from '#styled.ts';
import { ConditionalTextLink } from '../../ConditionalTextLink/index.tsx';
import { Overview } from '../../Overview/index.tsx';
import { PersonCreditsCarousel } from '../../PersonCreditsCarousel/index.tsx';
import { PopularityBadge } from '../../PopularityBadge/index.tsx';
import { PosterImage } from '../../PosterImage/index.tsx';
import { ProfilesCarousel } from '../../ProfilesCarousel/index.tsx';
import {
  DetailsBody,
  PosterImageContainer,
  PosterImageOverviewContainer,
  RatingsContainer,
  SectionA,
} from '../styled.ts';
import { type Props } from './types.ts';

export const PersonDetails = ({ data, setActivePreview }: Props) => {
  const { biography, birthday, deathday, id, knownForDepartment, name, placeOfBirth, popularity, profilePath } = data;
  const theme = useTheme();
  const { config } = useToolbelt();
  const isAncestorSlideoutDrawer = useIsAncestor(Ancestor.ListingPreview);

  return useMemo(
    () => (
      <>
        <Section threeQuarterWidthOnDesktop={!isAncestorSlideoutDrawer}>
          <Typography sx={{ paddingRight: '52px', position: 'relative' }} variant="h4">
            <ConditionalTextLink link={isAncestorSlideoutDrawer} to={`/details/person/${String(id)}`}>
              <strong>{name}</strong>
            </ConditionalTextLink>
            {isAncestorSlideoutDrawer ? (
              <IconButton
                aria-label="Open preview in full"
                component={Link}
                sx={{
                  color: theme.palette.grey[100],
                  position: 'absolute',
                  right: 0,
                  top: 0,
                }}
                to={`/details/person/${String(id)}`}
              >
                <ArrowForwardIos />
              </IconButton>
            ) : undefined}
          </Typography>
          <div>
            <Typography sx={{ color: theme.supportingTextPrimary }} variant="body2">
              <span>{knownForDepartment}</span>
              {birthday && (
                <>
                  <FiberManualRecord
                    sx={{ height: '10px', marginLeft: theme.spacing(1), marginRight: theme.spacing(1), width: '10px' }}
                  />
                  <span>{formatAge(birthday, deathday)}</span>
                </>
              )}
              {placeOfBirth && (
                <>
                  <FiberManualRecord
                    sx={{ height: '10px', marginLeft: theme.spacing(1), marginRight: theme.spacing(1), width: '10px' }}
                  />
                  <span>{placeOfBirth}</span>
                </>
              )}
            </Typography>
          </div>
          <SectionA sx={{ display: 'flex', marginBottom: theme.spacing(1.5), marginTop: theme.spacing(1.5) }}>
            <RatingsContainer>
              <PopularityBadge
                popularity={popularity}
                sx={{
                  [theme.breakpoints.up('md')]: {
                    marginLeft: theme.spacing(2),
                  },
                  [theme.breakpoints.down('sm')]: {
                    display: 'none',
                  },
                  marginLeft: theme.spacing(1),
                }}
              />
            </RatingsContainer>
          </SectionA>
          <Divider sx={{ marginBottom: 0 }} />
        </Section>
        <Section threeQuarterWidthOnDesktop={!isAncestorSlideoutDrawer}>
          <DetailsBody>
            <PosterImageContainer>
              <PosterImage
                id={id}
                src={buildImagePath(profilePath, ImageSize.Poster, PosterSize.MD, config.images)}
                title={name}
                type={SectionType.Person}
              />
            </PosterImageContainer>
            <PosterImageOverviewContainer>
              <Overview content={biography} contentType="biography" id={id} name={name} />
            </PosterImageOverviewContainer>
          </DetailsBody>
        </Section>
        {!isAncestorSlideoutDrawer && (
          <>
            <PersonCreditsCarousel id={id} setActivePreview={setActivePreview} subType="cast" type={ScreenType.Movie}>
              {castCarousel =>
                castCarousel ? <Section title="Movies appeared in">{castCarousel}</Section> : undefined
              }
            </PersonCreditsCarousel>
            <PersonCreditsCarousel id={id} setActivePreview={setActivePreview} subType="crew" type={ScreenType.Movie}>
              {crewCarousel =>
                crewCarousel ? <Section title="Movies involved with">{crewCarousel}</Section> : undefined
              }
            </PersonCreditsCarousel>
            <PersonCreditsCarousel id={id} setActivePreview={setActivePreview} subType="cast" type={ScreenType.Tv}>
              {castCarousel =>
                castCarousel ? <Section title="TV shows appeared in">{castCarousel}</Section> : undefined
              }
            </PersonCreditsCarousel>
            <PersonCreditsCarousel id={id} setActivePreview={setActivePreview} subType="crew" type={ScreenType.Tv}>
              {crewCarousel =>
                crewCarousel ? <Section title="TV shows involved with">{crewCarousel}</Section> : undefined
              }
            </PersonCreditsCarousel>
            <ProfilesCarousel id={id} name={name}>
              {profilesCarousel => <Section title="Photos">{profilesCarousel}</Section>}
            </ProfilesCarousel>
          </>
        )}
      </>
    ),
    // We only want to re-execute when id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id],
  );
};
